import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Check, Wallet } from 'lucide-react';
import { format } from 'date-fns';
import { PromoRewardNotice, MeetingRoomRewardPanel } from './PromoRewardNotice';
import { getPromotion, meetingRoomRewardFromBackend } from '../../lib/promotions';

const formatCountdown = (ms) => {
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const PaymentStep = ({ bookingResult, handlePayment, paymentExpired, paymentMsLeft, onStartNewBooking }) => {
  const pr = bookingResult.promo_reward;
  const meetingRoomReward = meetingRoomRewardFromBackend(pr, bookingResult.total_amount);
  const promoReward = pr && pr.offer !== 'meeting_room_2_plus_2' ? {
    freePasses: pr.free_passes,
    multiDate: !!bookingResult.dates,
    eligible: pr.eligible,
    validUntil: pr.valid_until,
    saleName: getPromotion()?.name,
  } : null;
  const navigate = useNavigate();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-success" />
        </div>
        <h2 className="font-heading text-2xl font-bold mb-2">
          {bookingResult.requires_payment ? 'Complete Payment' : 'Booking Confirmed!'}
        </h2>
        <p className="text-muted-foreground">
          {bookingResult.requires_payment
            ? 'Your booking is reserved. Please complete payment to confirm.'
            : 'Your booking has been confirmed successfully.'}
        </p>
      </div>

      <Card className="bg-card border-border max-w-md mx-auto">
        <CardContent className="p-6 space-y-4">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Booking ID</span>
            <span className="font-mono text-sm">{bookingResult.id?.slice(0, 8)}...</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status</span>
            <Badge className={bookingResult.status === 'confirmed' ? 'bg-success' : 'bg-warning'}>
              {bookingResult.status}
            </Badge>
          </div>
          {bookingResult.dates && (
            <div className="flex justify-between gap-4" data-testid="payment-step-dates">
              <span className="text-muted-foreground">Dates</span>
              <span className="text-right text-sm">
                {bookingResult.dates.map(d => format(new Date(`${d}T00:00:00`), 'd MMM')).join(', ')}
                <span className="block text-xs text-muted-foreground">
                  {bookingResult.total_passes} Day Passes · one payment, one bill
                </span>
              </span>
            </div>
          )}
          {bookingResult.credits_used > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Credits Used</span>
              <span>₹{bookingResult.credits_used.toFixed(2)}</span>
            </div>
          )}
          {bookingResult.requires_payment && (
            <div className="flex justify-between text-lg font-semibold">
              <span>Amount Due</span>
              <span className="text-primary">₹{bookingResult.total_amount.toFixed(2)}</span>
            </div>
          )}
          {bookingResult.requires_payment && !paymentExpired && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Time left to pay</span>
              <span>{formatCountdown(paymentMsLeft)}</span>
            </div>
          )}
          {!paymentExpired && (
            <>
              <PromoRewardNotice reward={promoReward} granted={bookingResult.status === 'confirmed'} />
              <MeetingRoomRewardPanel reward={meetingRoomReward} eligible={!!pr?.eligible} granted={bookingResult.status === 'confirmed'} />
            </>
          )}
        </CardContent>
      </Card>

      {bookingResult.requires_payment && paymentExpired && (
        <p className="text-center text-sm text-destructive">
          This booking has expired because payment was not completed in time. Please start a new booking.
        </p>
      )}

      <div className="flex justify-center gap-4">
        {bookingResult.requires_payment && !paymentExpired && (
          <Button onClick={handlePayment} className="bg-primary hover:bg-primary/90" data-testid="pay-now-btn">
            <Wallet className="w-4 h-4 mr-2" /> Pay Now
          </Button>
        )}
        {bookingResult.requires_payment && paymentExpired && (
          <Button onClick={onStartNewBooking} className="bg-primary hover:bg-primary/90" data-testid="new-booking-btn">
            Start New Booking
          </Button>
        )}
        {!bookingResult.requires_payment && (
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
            Go to Dashboard
          </Button>
        )}
      </div>
    </div>
  );
};
